import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  CompletionStatus,
  ConflictSummary,
  DataExtractionConflictService,
} from '../../../services/data-extraction-conflict.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';
import { DataExtractionAgreementCardComponent } from '../data-extraction-agreement-card/data-extraction-agreement-card.component';

/**
 * PI conflicts overview for data extraction. Lists papers awaiting a PI decision
 * (unresolved conflicts) and, behind a toggle, the conflicts resolved so far.
 * Offers a gated "Complete Data Extraction" action that finalizes the project.
 */
@Component({
  selector: 'app-project-data-extraction-conflicts',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, ConfirmModalComponent, DataExtractionAgreementCardComponent],
  templateUrl: './project-data-extraction-conflicts.component.html',
})
export class ProjectDataExtractionConflictsComponent implements OnInit {
  private readonly conflictService = inject(DataExtractionConflictService);
  private readonly projectService = inject(ProjectService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  projectId = 0;

  readonly conflicts = signal<ConflictSummary[]>([]);
  readonly status = signal<CompletionStatus | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly piOnlyNotice = signal<string | null>(null);
  readonly showResolved = signal(false);
  readonly resolvedQuery = signal('');
  readonly completing = signal(false);
  readonly confirmOpen = signal(false);
  readonly completeError = signal<string | null>(null);
  readonly projectState = signal<string | null>(null);
  readonly completedAt = signal<string | null>(null);

  readonly unresolved = computed(() => this.conflicts().filter((c) => !c.resolved));
  readonly resolved = computed(() => this.conflicts().filter((c) => c.resolved));
  readonly unresolvedCount = computed(() => this.unresolved().length);
  readonly resolvedCount = computed(() => this.resolved().length);

  readonly isCompleted = computed(() => this.projectState() === 'COMPLETED');
  readonly showBackToDataExtraction = computed(() => this.projectState() === 'DATA_EXTRACTION');

  readonly filteredResolved = computed(() => {
    const q = this.resolvedQuery().trim().toLowerCase();
    if (!q) return this.resolved();
    return this.resolved().filter((c) => {
      const year = c.paper.publicationDate ? new Date(c.paper.publicationDate).getFullYear() : '';
      const haystack = `${c.paper.title} ${c.paper.authors ?? ''} ${year}`.toLowerCase();
      return haystack.includes(q);
    });
  });

  readonly canComplete = computed(
    () => !!this.status() && this.status()!.canComplete && this.unresolvedCount() === 0,
  );

  readonly completeBlockedReason = computed<string | null>(() => {
    if (this.canComplete()) return null;
    const s = this.status();
    const parts: string[] = [];
    const needsSecond = s?.papersNeedingSecondReviewer ?? 0;
    // Prefer the client-side unresolved count; fall back to the server figure.
    const unresolved = this.unresolvedCount() || (s?.unresolvedConflicts ?? 0);
    if (needsSecond > 0) {
      parts.push(
        `${needsSecond} paper${needsSecond > 1 ? 's' : ''} still need${needsSecond > 1 ? '' : 's'} a second reviewer`,
      );
    }
    if (unresolved > 0) {
      parts.push(`${unresolved} conflict${unresolved > 1 ? 's' : ''} still unresolved`);
    }
    if (parts.length === 0) {
      return 'Data extraction cannot be completed yet.';
    }
    return parts.join('; ') + '.';
  });

  ngOnInit(): void {
    this.projectId = Number(this.route.parent?.snapshot.paramMap.get('projectId'));
    this.loadProject();
  }

  private loadProject(): void {
    this.projectService.getProjectById(String(this.projectId)).subscribe({
      next: (project) => {
        if (project && project.userRole !== 'PI') {
          this.piOnlyNotice.set("This page is available to the project's PI only.");
          this.loading.set(false);
          return;
        }
        this.projectState.set(project?.state ?? null);
        this.completedAt.set(project?.completedAt ?? null);
        this.loadConflicts();
        this.loadStatus();
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          void this.router.navigate(['/login']);
          return;
        }
        this.loadError.set(httpErrorToMessage(err));
        this.loading.set(false);
      },
    });
  }

  private loadConflicts(): void {
    this.loading.set(true);
    this.conflictService.listConflicts(this.projectId).subscribe({
      next: (summaries) => {
        this.conflicts.set(summaries);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          void this.router.navigate(['/login']);
          return;
        }
        this.loadError.set(httpErrorToMessage(err));
        this.loading.set(false);
      },
    });
  }

  private loadStatus(): void {
    this.conflictService.completionStatus(this.projectId).subscribe({
      next: (s) => this.status.set(s),
      error: () => {
        // The button stays disabled when the gate cannot be read.
        this.status.set(null);
      },
    });
  }

  toggleResolved(): void {
    this.showResolved.update((v) => !v);
  }

  onCompleteClick(): void {
    if (!this.canComplete() || this.completing()) return;
    this.completeError.set(null);
    this.confirmOpen.set(true);
  }

  onConfirmOpenChange(open: boolean): void {
    this.confirmOpen.set(open);
  }

  navigateBackToDataExtraction(): void {
    void this.router.navigate(['/projects', this.projectId, 'data-extraction']);
  }

  async onConfirmComplete(): Promise<void> {
    if (this.completing()) return;
    this.completing.set(true);
    this.completeError.set(null);
    try {
      await firstValueFrom(this.conflictService.completeDataExtraction(this.projectId));
      this.confirmOpen.set(false);
      this.toast.success('Data extraction completed', 'The project is now read-only.');
      this.projectService.notifyProjectUpdated();
      void this.router.navigate(['/projects', this.projectId, 'completed']);
    } catch (err: unknown) {
      this.confirmOpen.set(false);
      if (err instanceof HttpErrorResponse && err.status === 401) {
        void this.router.navigate(['/login']);
        return;
      }
      // A 409 here is the server's source-of-truth rejection (e.g. a slow reviewer
      // finished a conflicting extraction between load and click). Surface the
      // server's specific reason when present.
      this.completeError.set(this.backendMessage(err, () => httpErrorToMessage(err)));
    } finally {
      this.completing.set(false);
    }
  }

  /**
   * Surface the backend's natural-language reason (ProblemDetail `detail`, or
   * `message`) when present, falling back to the shared generic mapper (which
   * still distinguishes an unreachable server). Avoids editing the shared helper.
   */
  private backendMessage(err: unknown, fallback: () => string): string {
    const body = (err as { error?: { detail?: unknown; message?: unknown } })?.error;
    const detail = body?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    const message = body?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return fallback();
  }
}
